import { useCallback, useEffect, useRef, useState } from 'react';
import { useConfirm } from './ui/ConfirmDialog';

const iconBtnStyle = {
  width: '32px',
  height: '32px',
  padding: 0,
  border: '1px solid var(--hairline)',
  borderRadius: 'var(--radius-md)',
  background: 'var(--canvas)',
  color: 'var(--muted)',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background 0.16s, color 0.16s',
};

/**
 * Generic side drawer that slides in from the right.
 *
 *   <Drawer
 *     open={mode !== null}
 *     onClose={() => setMode(null)}
 *     title="New client"
 *     subtitle="Add client info and contacts"
 *     footer={<><button className="btn-primary">Save</button>...</>}
 *   >
 *     ...body...
 *   </Drawer>
 *
 * If the body contains a <form data-dirty="true">, closing via ESC, the
 * overlay, or the X button asks for confirmation first, so accidental
 * dismissal cannot silently discard edits. An explicit Cancel button in the
 * footer still closes immediately.
 */
function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  width = 640,
  expandedWidth = 1100,    // when user clicks the expand toggle
}) {
  const panelRef = useRef(null);
  const previousFocusRef = useRef(null);
  const confirmingRef = useRef(false);
  const [expanded, setExpanded] = useState(false);
  const confirm = useConfirm();

  // Always start collapsed when re-opening.
  useEffect(() => { if (!open) setExpanded(false); }, [open]);

  // Guarded close for accidental dismissals (ESC / overlay / X).
  const requestClose = useCallback(async () => {
    if (confirmingRef.current) return;
    const dirtyForm = panelRef.current?.querySelector('form[data-dirty="true"]');
    if (dirtyForm) {
      confirmingRef.current = true;
      const ok = await confirm({
        title: 'Discard changes?',
        message: 'You have unsaved changes. If you close now they will be lost.',
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        danger: true,
      });
      confirmingRef.current = false;
      if (!ok) return;
    }
    onClose?.();
  }, [confirm, onClose]);

  // ESC closes; restore body scroll on close. Move focus into the panel on
  // open and hand it back to the triggering element on close.
  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement;
    if (panelRef.current) panelRef.current.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') requestClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === 'function') prev.focus();
    };
  }, [open, requestClose]);

  const effectiveMax = expanded ? expandedWidth : width;

  return (
    <>
      {/* Overlay */}
      <div
        onClick={requestClose}
        aria-hidden="true"
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(24, 23, 21, 0.42)',
          backdropFilter: 'blur(2px)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: 'opacity 0.22s ease',
          zIndex: 1000,
        }}
      />

      {/* Panel */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: `min(${effectiveMax}px, ${expanded ? '92vw' : '95vw'})`,
          maxWidth: '100vw',
          background: 'var(--canvas)',
          borderLeft: '1px solid var(--hairline)',
          boxShadow: open ? '-16px 0 48px rgba(20,20,19,0.10)' : 'none',
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s cubic-bezier(0.32, 0.72, 0, 1), width 0.24s ease',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          outline: 'none',
        }}
      >
        {/* Header */}
        <div style={{
          flexShrink: 0,
          padding: '20px 28px',
          borderBottom: '1px solid var(--hairline)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '16px',
          background: 'var(--canvas)',
        }}>
          <div style={{ minWidth: 0 }}>
            {title && (
              <h2 style={{
                margin: 0,
                fontFamily: 'var(--font-serif)',
                fontSize: '26px',
                fontWeight: 500,
                color: 'var(--ink)',
                letterSpacing: '-0.4px',
                lineHeight: 1.2,
              }}>
                {title}
              </h2>
            )}
            {subtitle && (
              <div style={{
                marginTop: '4px',
                fontFamily: 'var(--font-sans)',
                fontSize: '13px',
                color: 'var(--muted)',
              }}>
                {subtitle}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setExpanded(e => !e)}
              aria-label={expanded ? 'Collapse drawer' : 'Expand drawer'}
              title={expanded ? 'Collapse' : 'Expand'}
              style={iconBtnStyle}
              onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.color = 'var(--ink)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'var(--canvas)'; e.currentTarget.style.color = 'var(--muted)'; }}
            >
              {expanded ? (
                // Collapse: arrows pointing inward
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="4 14 10 14 10 20" />
                  <polyline points="20 10 14 10 14 4" />
                  <line x1="14" y1="10" x2="21" y2="3" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              ) : (
                // Expand: arrows pointing outward
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="15 3 21 3 21 9" />
                  <polyline points="9 21 3 21 3 15" />
                  <line x1="21" y1="3" x2="14" y2="10" />
                  <line x1="3" y1="21" x2="10" y2="14" />
                </svg>
              )}
            </button>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              title="Close"
              style={iconBtnStyle}
              onMouseOver={(e) => { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.color = 'var(--ink)'; }}
              onMouseOut={(e) => { e.currentTarget.style.background = 'var(--canvas)'; e.currentTarget.style.color = 'var(--muted)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        {/* Body (scrollable) */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px 28px',
          background: 'var(--canvas)',
        }}>
          {children}
        </div>

        {/* Footer (sticky) */}
        {footer && (
          <div style={{
            flexShrink: 0,
            padding: '16px 28px',
            borderTop: '1px solid var(--hairline)',
            background: 'var(--surface-soft)',
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
          }}>
            {footer}
          </div>
        )}
      </aside>
    </>
  );
}

export default Drawer;
